import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { periodeFr } from '../../lib/periode-conges';
import { congesApi, type DemandeAbsence } from '../../services/conges-api';
import { MessageErreur } from '../../components/ndf/ui/MessageEtat';
import { BoutonPrincipal } from '../../components/ndf/ui/Champ';

/**
 * « Mes demandes » — en cours d'abord, historique ensuite (specs §D).
 *
 * Ce qui attend une décision est en haut : c'est la seule chose sur laquelle le
 * salarié peut encore agir. Le reste est de la consultation.
 */

export const LIBELLE_STATUT: Record<string, string> = {
  brouillon: 'Brouillon',
  attente_manager: 'En attente du manager',
  attente_rh: 'En attente de la RH',
  approuvee: 'Approuvée',
  refusee: 'Rejetée',
  annulee: 'Annulée',
};

/** Un badge par statut, de couleur CONSTANTE partout dans l'application. */
export const COULEUR_STATUT: Record<string, string> = {
  brouillon: 'bg-slate-100 text-slate-700',
  attente_manager: 'bg-jaune-fond/40 text-jaune',
  attente_rh: 'bg-jaune-fond/40 text-jaune',
  approuvee: 'bg-vert-fond/40 text-vert',
  refusee: 'bg-corail-fond/40 text-corail',
  annulee: 'bg-slate-100 text-slate-500',
};

export function dateFr(iso: string): string {
  const [a, m, j] = iso.split('-');
  return `${j}/${m}/${a}`;
}

function Ligne({ d }: { d: DemandeAbsence }) {
  return (
    <li>
      <Link
        to={`/conges/demandes/${d.id}`}
        className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white p-3 transition-colors hover:bg-ciel/10"
      >
        <div className="min-w-0">
          <p className="font-medium text-nuit">{d.type_libelle ?? 'Absence'}</p>
          <p className="text-sm text-nuit/60">
            {periodeFr(d)}
            {d.description ? ` (${d.description})` : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {d.a_posteriori && (
            <span className="rounded-full bg-jaune-fond/40 px-2 py-0.5 text-xs font-medium text-jaune">
              a posteriori
            </span>
          )}
          {d.demande_liee_id && (
            <span className="rounded-full bg-violet-fond/30 px-2 py-0.5 text-xs font-medium text-violet">
              demande liée
            </span>
          )}
          <span className="tabular-nums text-sm text-nuit/70">{d.nb_jours} j</span>
          <span
            className={`rounded-full px-2 py-0.5 text-xs font-medium ${
              COULEUR_STATUT[d.statut] ?? 'bg-slate-100 text-slate-700'
            }`}
          >
            {LIBELLE_STATUT[d.statut] ?? d.statut}
          </span>
        </div>
      </Link>
    </li>
  );
}

export function CongesDemandesPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['conges', 'demandes'],
    queryFn: () => congesApi.demandes.liste(),
  });

  if (isLoading) return <p className="p-6 text-nuit/60">Chargement…</p>;
  if (error) return <div className="p-6"><MessageErreur erreur={error} /></div>;

  const demandes = data?.demandes ?? [];
  const enCours = demandes.filter((d) => d.statut.startsWith('attente_') || d.statut === 'brouillon');
  const historique = demandes.filter((d) => !enCours.includes(d));

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-4 md:p-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-titre text-xl font-bold text-nuit">Mes demandes d’absence</h1>
        <Link to="/conges/poser">
          <BoutonPrincipal type="button">Poser une absence</BoutonPrincipal>
        </Link>
      </header>

      <section>
        <h2 className="mb-2 font-titre text-base font-semibold text-nuit">En cours</h2>
        {enCours.length === 0 ? (
          <p className="rounded-lg border border-slate-200 bg-white p-4 text-sm text-nuit/60">
            Aucune demande en attente.
          </p>
        ) : (
          <ul className="space-y-2">
            {enCours.map((d) => (
              <Ligne key={d.id} d={d} />
            ))}
          </ul>
        )}
      </section>

      <section>
        <h2 className="mb-2 font-titre text-base font-semibold text-nuit">Historique</h2>
        {historique.length === 0 ? (
          <p className="rounded-lg border border-slate-200 bg-white p-4 text-sm text-nuit/60">
            Rien pour l’instant.
          </p>
        ) : (
          <ul className="space-y-2">
            {historique.map((d) => (
              <Ligne key={d.id} d={d} />
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
